import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AllInclusiveService } from '@app/core/services/all-inclusive.service';
import { BlFrontendButtonComponent } from '@app/core/wrappers/bl-frontend-button.component';
import { validateEmail } from '@app/shared/misc/email-validator';
import { TranslocoModule } from '@jsverse/transloco';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from 'src/app/core/i18n/translatable-form-dialog-base.component';
import { BlFrontendEmailInputComponent } from 'src/app/core/wrappers/bl-frontend-email-input.component';
import { SafeHtmlPipe } from 'src/app/shared/pipes/safe-html.pipe';
import { DialogData, DialogResult } from '../../form-dialog-base/form-dialog-base.component';

interface ConfirmDialogData extends DialogData {
  email: string;
}

interface Result extends DialogResult {
  confirmed: boolean;
  email?: string;
}

const TRANSLATION_SCOPE = 'createClouddbConfirmationDialog';

@Component({
  selector: 'app-create-clouddb-confirmation-dialog',
  template: `
    <div class="tabbed-dialog-layout" *transloco="let t; prefix: scope">
      <div class="tabbed-dialog-body">
        <ng-container *ngIf="isLoggedInWithBLTAService; else hhlTemplate">
          <p [innerHTML]="t('confirmCreationMessage1') | safeHtml"></p>
          <p [innerHTML]="t('confirmCreationMessage2') | safeHtml"></p>
          <p [innerHTML]="t('confirmCreationMessage3') | safeHtml"></p>
          <div class="flex flex-col mt-4">
            <label class="font-bold block" for="email">{{ t('emailToCompany') }} *</label>
            <bl-frontend-email-input
              class="w-full"
              id="email"
              name="email"
              [(ngModel)]="email"
              [placeholder]="t('emailAddress')" />
          </div>
        </ng-container>
        <ng-template #hhlTemplate>
          <p class="font-bold" [innerHTML]="t('confirmCreationHHLMessage1') | safeHtml"></p>
          <p class="font-bold" [innerHTML]="t('confirmCreationHHLMessage2') | safeHtml"></p>
          <p class="font-bold" [innerHTML]="t('confirmCreationHHLMessage3') | safeHtml"></p>
        </ng-template>
      </div>
      <div class="tabbed-dialog-actions flex justify-end items-end p-4 bg-bl-grey-100">
        <bl-frontend-button
          [text]="'shared.cancel' | transloco"
          variant="secondary"
          (onclick)="close({ confirmed: false })" />
        <bl-frontend-button
          [text]="'shared.ok' | transloco"
          [disabled]="!isValid"
          (onclick)="close({ confirmed: true, email })" />
      </div>
    </div>
  `,
  standalone: true,
  providers: [DialogService, AllInclusiveService, getTranslationProvider(TRANSLATION_SCOPE)],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BlFrontendButtonComponent,
    BlFrontendEmailInputComponent,
    TranslocoModule,
    SafeHtmlPipe,
  ],
})
export class CreateClouddbConfirmationDialog extends TranslatableFormDialogBaseComponent<ConfirmDialogData, Result> {
  email: string;

  get isLoggedInWithBLTAService() {
    return this.allInclusiveService.isLoggedInWithBLTAService;
  }

  get isLoggedInWithAllInclusiveService() {
    return this.allInclusiveService.isLoggedInWithAllInclusiveService;
  }

  get isValid(): boolean {
    if (this.isLoggedInWithAllInclusiveService) {
      return true;
    }

    return this.isLoggedInWithBLTAService && validateEmail(this.email);
  }

  constructor(
    protected override ref: DynamicDialogRef,
    protected override config: DynamicDialogConfig<ConfirmDialogData>,
    private allInclusiveService: AllInclusiveService,
  ) {
    super(ref, config);
    this.email = config.data.email;
  }

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(
    translation: Record<string, string>,
  ): Omit<DynamicDialogConfig<DialogData>, 'data'> {
    const allInclusiveService = new AllInclusiveService(null);
    let header = translation.confirmCreationOfCloudDatabase;

    if (allInclusiveService.isLoggedInWithBLTAService) {
      header = translation.confirmActivationOfNewCollaborationPackage;
    }

    return {
      header,
      width: '500px',
      closable: false,
      closeOnEscape: false,
      draggable: false,
      modal: true,
      style: { maxHeight: '90vh' },
      styleClass: 'create-clouddb-confirmation-dialog tabbed-dialog',
    };
  }
}
